import React, { useMemo } from 'react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { Table } from '@sede-enterprise/shell-ds-react-framework';
import {
  TBody,
  TH as Th,
  THead,
  TR as Tr,
  TD as Td,
} from '@sede-enterprise/shell-ds-react-framework/build/esm/components/Table/components/TableElements/TableElements';
import Loader from 'src/UI/Loader';
import Button from 'src/UI/Button';
import { IProfileData } from './types';
import { CET_TIME_ZONE } from 'src/constants/Common';
import { ProfileModalGlobalStyle } from './style';
import { Modal } from '../../SystemAdmin/NominationGroups/style';

dayjs.extend(utc);
dayjs.extend(timezone);

interface ProfileDataModalProps {
  isOpen: boolean;
  dealLegId?: string;
  profileData: IProfileData[];
  isLoading: boolean;
  onClose: () => void;
}

const formatEpochToCET = (epochMs: number): string => {
  const d = dayjs(epochMs).tz(CET_TIME_ZONE);
  return d.isValid() ? d.format('DD MMM YYYY HH:mm') : 'N/A';
};

const formatQuantity = (value: number): string => {
  return Math.round(value).toLocaleString('en-GB');
};

export const ProfileDataModal: React.FC<ProfileDataModalProps> = ({
  isOpen,
  dealLegId,
  profileData,
  isLoading,
  onClose,
}) => {
  const displayData = useMemo(() => profileData || [], [profileData]);

  if (!isOpen) {
    return null;
  }

  let bodyContent: React.ReactNode;
  if (isLoading) {
    bodyContent = (
      <div className='profile-modal-loading'>
        <Loader />
      </div>
    );
  } else if (displayData.length === 0) {
    bodyContent = <div className='profile-modal-empty'>No profile data available</div>;
  } else {
    bodyContent = (
      <div className='profile-modal-table-scroll'>
        <Table className='profile-modal-table' size='small'>
          <THead>
            <Tr>
              <Th>Period From</Th>
              <Th>Period To</Th>
              <Th>Quantity (KWh)</Th>
            </Tr>
          </THead>
          <TBody>
            {displayData.map((profile) => (
              <Tr key={`${profile.periodFromEpoch}-${profile.periodToEpoch}`}>
                <Td>{formatEpochToCET(profile.periodFromEpoch)}</Td>
                <Td>{formatEpochToCET(profile.periodToEpoch)}</Td>
                <Td style={{ fontVariantNumeric: 'tabular-nums' }}>
                  {formatQuantity(profile.quantity)}
                </Td>
              </Tr>
            ))}
          </TBody>
        </Table>
      </div>
    );
  }

  return (
    <>
      <ProfileModalGlobalStyle />
      <Modal
        className='deal-quantity-modal'
        title='Flat Deal Quantity'
        open={isOpen}
        onClose={onClose}
        maskClosable={false}
        isDraggable
        width={600}
        footer={
          <div className='button-style'>
            <Button variant='outlined' onClick={onClose}>
              Close
            </Button>
          </div>
        }
      >
        <div className='profile-modal-body'>
          <div className='deal-id'>
            <strong>Deal Id:</strong> {dealLegId || 'N/A'}
          </div>
          {bodyContent}
        </div>
      </Modal>
    </>
  );
};

export default ProfileDataModal;
